/**
 * ManualRebateCSVUpload - Manual CSV upload flow for rebate invoices
 *
 * Provides a simple, fast-path upload for users with pre-formatted CSV files.
 * No AI processing - direct validation and submission.
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import Papa from 'papaparse';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeftIcon,
  DownloadIcon,
  UploadIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import paths from '@/paths';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import FileUploadDropzone from 'src/components/documents/FileUploadDropzone';
import SimpleDataTable, {
  SimpleDataTableColumn,
} from 'src/components/common/SimpleDataTable';
import useSubmitTransformedCSV from 'src/api/spreadsheet/useSubmitTransformedCSV';
import { useCheckOverlap } from 'src/api/spreadsheet/useCheckOverlap';
import { useProductGroupsBySupplier } from 'src/api/rebates/useProductGroups';
import { useToast } from 'src/context/ToastContext';
import {
  downloadRebateInvoiceTemplate,
  getRebateInvoiceTemplateInfo,
} from 'src/utils/csvTemplates';
import {
  REBATE_CSV_COLUMNS,
  REQUIRED_REBATE_CSV_COLUMNS,
  CURRENCY_CODE_VALUES,
  DEFAULT_CURRENCY_CODE,
} from '@erp/shared-types';
import type { OverlappingInvoice, OverlapResolution } from '@erp/shared-types';
import { applyCurrencyConversion } from './utils/spreadsheetUtils';
import posthog from 'posthog-js';
import { useCompanyBySupplier } from '@/api/core/companies/useCompanyBySupplier';
import { OverlapWarningModal } from 'src/components/spreadsheets/OverlapWarningModal';
import {
  ALL_OTHER_PRODUCTS,
  NON_REBATABLE_PRODUCTS,
} from '@erp/shared-utils/src/constants';

interface ValidationError {
  rowIndex: number;
  field: string;
  error: string;
  value: unknown;
}

interface ValidationResult {
  columnErrors: string[];
  rowErrors: ValidationError[];
  valid: boolean;
}

/**
 * IMPORTANT: Currency handling is currently a temporary solution.
 * This component uses Company.metadata.defaultCurrency to pre-populate
 * the currency dropdown, but allows user override per-upload.
 *
 * TODO: Future refactoring needed for proper multi-currency support.
 * @see packages/shared-types/src/currency.ts for more details
 */

const ManualRebateCSVUpload: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Form state
  const [supplierId, setSupplierId] = useState<string | undefined>(undefined);
  const [currency, setCurrency] = useState<string>('');

  // Fetch company to get default currency when supplier is selected
  // IMPORTANT: Currency now comes from Company.metadata.defaultCurrency
  // This is a temporary solution - future refactoring should implement
  // proper multi-currency support at the organization level.
  const { data: supplierCompany } = useCompanyBySupplier(supplierId);

  // Update currency when company is fetched
  useEffect(() => {
    if (supplierCompany?.metadata?.defaultCurrency) {
      setCurrency(supplierCompany.metadata.defaultCurrency);
    } else if (supplierId) {
      // Fallback to CAD if company has no default currency set
      setCurrency(DEFAULT_CURRENCY_CODE);
    }
  }, [supplierCompany, supplierId]);

  // File and data state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<Record<string, unknown>[]>([]);

  // Currency conversion state (similar to AI workflow)
  const [conversionRate, setConversionRate] = useState<number | undefined>(
    undefined,
  );
  const [appliedConversionRate, setAppliedConversionRate] = useState<
    number | undefined
  >(undefined);
  const [preConversionData, setPreConversionData] = useState<
    Record<string, unknown>[]
  >([]);
  const [conversionApplied, setConversionApplied] = useState(false);

  // API
  const { mutateAsync: submitCSV, isLoading: isSubmitting } =
    useSubmitTransformedCSV();
  const { mutateAsync: checkOverlap, isLoading: isCheckingOverlap } =
    useCheckOverlap();

  // Overlap detection state
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlappingInvoices, setOverlappingInvoices] = useState<
    OverlappingInvoice[]
  >([]);
  const [totalAffectedLineItems, setTotalAffectedLineItems] = useState(0);
  // Store pending submission data for retry after overlap resolution
  const pendingSubmissionRef = useRef<{
    transformedData: string;
  } | null>(null);

  // Fetch product groups for the selected supplier
  const { data: productGroupsData } = useProductGroupsBySupplier(
    supplierId || '',
    !!supplierId,
  );

  const productGroups = useMemo(
    () => productGroupsData || [],
    [productGroupsData],
  );

  const templateInfo = useMemo(() => getRebateInvoiceTemplateInfo(), []);

  // Check if CSV has Value column
  const hasValueColumn = useMemo(() => {
    return (
      csvData.length > 0 &&
      Object.keys(csvData[0]).includes(REBATE_CSV_COLUMNS.VALUE)
    );
  }, [csvData]);

  // Detect unapplied conversion changes
  const hasUnappliedConversionChanges = useMemo(() => {
    if (!csvData.length) return false;
    if (!currency || currency === 'CAD') return false;
    if (!hasValueColumn) return false;

    // Check if rate changed from applied rate
    return conversionRate !== appliedConversionRate;
  }, [
    conversionRate,
    appliedConversionRate,
    csvData,
    currency,
    hasValueColumn,
  ]);

  const handleBack = () => {
    navigate({ to: paths.authenticated.rebates.uploadData });
  };

  const handleDownloadTemplate = useCallback(() => {
    downloadRebateInvoiceTemplate();
    posthog.capture('manual_csv_template_downloaded', {
      context: 'manual_rebate_csv_upload',
    });
  }, []);

  // Helper function to apply conversion
  const applyConversion = useCallback(
    (
      data: Record<string, unknown>[],
      rate: number,
      sourceCurrency: string,
    ): Record<string, unknown>[] => {
      if (data.length === 0) return data;

      // Convert to unknown[][] format for applyCurrencyConversion
      const headers = Object.keys(data[0]);
      const rows = [headers, ...data.map((row) => headers.map((h) => row[h]))];

      // Apply conversion
      const convertedRows = applyCurrencyConversion(
        rows,
        rate,
        sourceCurrency,
        REBATE_CSV_COLUMNS.VALUE,
        'CAD',
      );

      // Convert back to Record format
      const newHeaders = convertedRows[0] as string[];
      return convertedRows.slice(1).map((row) => {
        const obj: Record<string, unknown> = {};
        newHeaders.forEach((header, idx) => {
          obj[String(header)] = row[idx];
        });
        return obj;
      });
    },
    [],
  );

  // Apply conversion handler
  const handleApplyConversion = useCallback(() => {
    if (!preConversionData.length || !conversionRate || !currency) return;

    const converted = applyConversion(
      preConversionData,
      conversionRate,
      currency,
    );
    setCsvData(converted);
    setAppliedConversionRate(conversionRate);
    setConversionApplied(true);

    posthog.capture('manual_csv_conversion_applied', {
      context: 'manual_rebate_csv_upload',
      source_currency: currency,
      conversion_rate: conversionRate,
    });
  }, [preConversionData, conversionRate, currency, applyConversion]);

  // Reset conversion handler
  const handleResetConversion = useCallback(() => {
    // Revert to pre-conversion data
    setCsvData([...preConversionData]);
    setConversionRate(appliedConversionRate);
    setConversionApplied(!!appliedConversionRate);
  }, [preConversionData, appliedConversionRate]);

  // Client-side validation
  const validateCSV = useCallback(
    (
      data: Record<string, unknown>[],
      validProductGroups: Array<{ id: string; name: string }> = [],
    ): ValidationResult => {
      const columnErrors: string[] = [];
      const rowErrors: ValidationError[] = [];

      if (data.length === 0) {
        return {
          columnErrors: ['CSV file is empty'],
          rowErrors: [],
          valid: false,
        };
      }

      // Check required columns exist
      const headers = Object.keys(data[0] || {});
      const missingColumns = REQUIRED_REBATE_CSV_COLUMNS.filter(
        (col) => !headers.includes(col),
      );

      if (missingColumns.length > 0) {
        columnErrors.push(
          `Missing required columns: ${missingColumns.join(', ')}`,
        );
      }

      // Validate each row
      data.forEach((row, idx) => {
        // Date validation
        const dateStr = row[REBATE_CSV_COLUMNS.DATE];
        if (!dateStr) {
          rowErrors.push({
            rowIndex: idx,
            field: REBATE_CSV_COLUMNS.DATE,
            error: 'Date is required',
            value: dateStr,
          });
        } else if (typeof dateStr === 'string') {
          // Check ISO date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(dateStr)) {
            rowErrors.push({
              rowIndex: idx,
              field: REBATE_CSV_COLUMNS.DATE,
              error: `Must be YYYY-MM-DD format (e.g., 2025-01-31). Excel users: Format cells as text or use TEXT(date,"YYYY-MM-DD") before exporting.`,
              value: dateStr,
            });
          } else {
            // Check if it's actually a valid date
            const parsedDate = new Date(dateStr);
            if (
              isNaN(parsedDate.getTime()) ||
              parsedDate.toISOString().slice(0, 10) !== dateStr
            ) {
              rowErrors.push({
                rowIndex: idx,
                field: REBATE_CSV_COLUMNS.DATE,
                error: 'Date does not exist (e.g., 2025-02-30 is invalid)',
                value: dateStr,
              });
            }
          }
        }

        // Division Name validation
        if (!row[REBATE_CSV_COLUMNS.DIVISION_NAME]) {
          rowErrors.push({
            rowIndex: idx,
            field: REBATE_CSV_COLUMNS.DIVISION_NAME,
            error: 'Division Name is required',
            value: row[REBATE_CSV_COLUMNS.DIVISION_NAME],
          });
        }

        // Product Group Name validation
        // Column must exist, but value can be empty, null, ALL_OTHER_PRODUCTS, or NON_REBATABLE_PRODUCTS
        const productGroupName = row[REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME];

        // Check if value is a special identifier (case-insensitive)
        const isSpecialIdentifier =
          productGroupName &&
          typeof productGroupName === 'string' &&
          (productGroupName.trim().toLowerCase() ===
            ALL_OTHER_PRODUCTS.toLowerCase() ||
            productGroupName.trim().toLowerCase() ===
              NON_REBATABLE_PRODUCTS.toLowerCase());

        if (
          productGroupName &&
          typeof productGroupName === 'string' &&
          productGroupName.trim() !== '' &&
          !isSpecialIdentifier
        ) {
          // If supplier HAS product groups, validate the name matches
          if (validProductGroups.length > 0) {
            const productGroupExists = validProductGroups.some(
              (pg) =>
                pg.name.toLowerCase() === productGroupName.toLowerCase().trim(),
            );

            if (!productGroupExists) {
              rowErrors.push({
                rowIndex: idx,
                field: REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME,
                error: `Product group "${productGroupName}" not found. Available: ${validProductGroups
                  .map((pg) => pg.name)
                  .join(', ')}.`,
                value: productGroupName,
              });
            }
          } else {
            // If supplier has NO product groups, only special identifiers or empty is valid
            rowErrors.push({
              rowIndex: idx,
              field: REBATE_CSV_COLUMNS.PRODUCT_GROUP_NAME,
              error: `This supplier has no product groups. Use "${ALL_OTHER_PRODUCTS}", "${NON_REBATABLE_PRODUCTS}", or leave empty.`,
              value: productGroupName,
            });
          }
        }

        // Value validation
        const value = row[REBATE_CSV_COLUMNS.VALUE];
        if (value === undefined || value === null || value === '') {
          rowErrors.push({
            rowIndex: idx,
            field: REBATE_CSV_COLUMNS.VALUE,
            error: 'Value is required',
            value,
          });
        } else {
          const numValue = Number(value);
          if (isNaN(numValue)) {
            rowErrors.push({
              rowIndex: idx,
              field: REBATE_CSV_COLUMNS.VALUE,
              error: 'Value must be a valid number',
              value,
            });
          }
        }
      });

      return {
        columnErrors,
        rowErrors,
        valid: columnErrors.length === 0 && rowErrors.length === 0,
      };
    },
    [],
  );

  // Compute validation errors from current data (derived state)
  const validationErrors = useMemo(() => {
    if (csvData.length > 0 && supplierId) {
      return validateCSV(csvData, productGroups);
    }
    return {
      columnErrors: [],
      rowErrors: [],
      valid: true,
    };
  }, [csvData, productGroups, supplierId, validateCSV]);

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const file = files[0];
      setUploadedFile(file);

      // Parse CSV
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const data = results.data as Record<string, unknown>[];

          // Store pre-conversion data
          setPreConversionData(data);

          // Check if 'Value' column exists
          const hasValue =
            data.length > 0 &&
            Object.keys(data[0]).includes(REBATE_CSV_COLUMNS.VALUE);

          // Auto-apply conversion if rate is set
          if (hasValue && conversionRate && currency && currency !== 'CAD') {
            const converted = applyConversion(data, conversionRate, currency);
            setCsvData(converted);
            setAppliedConversionRate(conversionRate);
            setConversionApplied(true);

            posthog.capture('manual_csv_conversion_auto_applied', {
              context: 'manual_rebate_csv_upload',
              source_currency: currency,
              conversion_rate: conversionRate,
            });
          } else {
            setCsvData(data);
            setConversionApplied(false);
          }

          posthog.capture('manual_csv_uploaded', {
            context: 'manual_rebate_csv_upload',
            rowCount: data.length,
            has_value_column: hasValue,
          });
        },
        error: (error) => {
          showToast({
            title: 'CSV Parse Error',
            message: error.message,
            color: 'error',
          });
        },
      });
    },
    [showToast, conversionRate, currency, applyConversion],
  );

  // Helper to check if a cell has an error
  const getCellError = useCallback(
    (rowIndex: number, field: string) => {
      return validationErrors.rowErrors.find(
        (err) => err.rowIndex === rowIndex && err.field === field,
      );
    },
    [validationErrors.rowErrors],
  );

  // Handle cell edits
  const handleCellChange = useCallback(
    (rowIndex: number, columnKey: string, newValue: unknown) => {
      const updatedData = [...csvData];
      updatedData[rowIndex] = {
        ...updatedData[rowIndex],
        [columnKey]: newValue,
      };
      setCsvData(updatedData);

      posthog.capture('manual_csv_cell_edited', {
        context: 'manual_rebate_csv_upload',
        column: columnKey,
        rowIndex,
      });
    },
    [csvData],
  );

  // Create columns for SimpleDataTable
  const tableColumns = useMemo((): SimpleDataTableColumn<
    Record<string, unknown>
  >[] => {
    if (csvData.length === 0) return [];

    const headers = Object.keys(csvData[0]);

    // Identify conversion columns
    const conversionColumns = [`Value ${currency}`, `Conversion Rate to CAD`];
    const isConversionColumn = (header: string) =>
      conversionColumns.includes(header);

    return headers.map((header) => {
      const isConversionCol = isConversionColumn(header);

      return {
        key: header,
        header: isConversionCol ? `💱 ${header}` : header,
        editable: true, // All columns are editable
        render: (row, index) => {
          const cellError = getCellError(index, header);
          const value = row[header];
          const displayValue =
            value === null || value === undefined ? '' : String(value);

          const isConversionCol = isConversionColumn(header);

          return (
            <div
              className={
                cellError
                  ? 'bg-red-50 border-l-2 border-red-500 px-2 py-1'
                  : isConversionCol
                    ? 'bg-blue-50 border-l-2 border-blue-400 px-2 py-1'
                    : ''
              }
            >
              <span
                className={
                  cellError
                    ? 'text-red-700'
                    : isConversionCol
                      ? 'text-blue-900 font-medium'
                      : ''
                }
              >
                {displayValue}
              </span>
              {cellError && (
                <p className="text-xs text-red-600 mt-1">{cellError.error}</p>
              )}
            </div>
          );
        },
        // Custom editable render to show input with error styling
        renderEditable: (value, rowIndex) => {
          const cellError = getCellError(rowIndex, header);
          return (
            <div>
              <Input
                value={
                  value === null || value === undefined ? '' : String(value)
                }
                onChange={(e) =>
                  handleCellChange(rowIndex, header, e.target.value)
                }
                className={
                  cellError ? 'border-red-500 focus:border-red-600' : ''
                }
              />
              {cellError && (
                <p className="text-xs text-red-600 mt-1">{cellError.error}</p>
              )}
            </div>
          );
        },
      };
    });
  }, [csvData, getCellError, handleCellChange, currency]);

  /**
   * Extract date range from CSV data for overlap checking
   */
  const extractDateRange = useCallback(
    (
      data: Record<string, unknown>[],
    ): { startDate: string; endDate: string } | null => {
      if (data.length === 0) return null;

      const dates: Date[] = [];
      for (const row of data) {
        const dateStr = row[REBATE_CSV_COLUMNS.DATE] as string;
        if (dateStr) {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            dates.push(date);
          }
        }
      }

      if (dates.length === 0) return null;

      const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

      return {
        startDate: minDate.toISOString().split('T')[0],
        endDate: maxDate.toISOString().split('T')[0],
      };
    },
    [],
  );

  /**
   * Perform the actual submission with optional overlap resolution
   */
  const performSubmission = useCallback(
    async (overlapResolution?: OverlapResolution) => {
      const pending = pendingSubmissionRef.current;
      if (!pending || !uploadedFile || !supplierId || !currency) {
        console.error('No pending submission data');
        return;
      }

      try {
        const response = await submitCSV({
          transformationType: 'rebate-invoices',
          constants: { supplierId, currency },
          transformedData: pending.transformedData,
          filename: uploadedFile.name,
          originalFiles: [uploadedFile],
          overlapResolution,
        });

        // Clear pending submission
        pendingSubmissionRef.current = null;

        posthog.capture('manual_csv_submit_completed', {
          context: 'manual_rebate_csv_upload',
          agreement_id: response.data.agreementId,
          line_items: response.data.totalLineItems,
          overlap_resolution: overlapResolution || 'none',
        });

        showToast({
          title: 'Upload Complete!',
          message: `Successfully submitted ${response.data.totalLineItems} line items.`,
          color: 'success',
          icon: 'celebration',
        });

        navigate({
          to: '/rebates/agreements/$id',
          params: { id: response.data.agreementId },
        });
      } catch (error) {
        console.error('Submit error:', error);

        const errorData = error as {
          response?: { data?: { error?: string } };
          message?: string;
        };

        const errorMessage =
          errorData?.response?.data?.error ||
          errorData?.message ||
          'Failed to submit CSV data';

        showToast({
          title: 'Submission Failed',
          message: errorMessage,
          color: 'error',
          persistent: true,
        });

        posthog.capture('manual_csv_submit_failed', {
          context: 'manual_rebate_csv_upload',
          error: errorMessage,
        });
      } finally {
        setOverlapModalOpen(false);
      }
    },
    [uploadedFile, supplierId, currency, submitCSV, showToast, navigate],
  );

  /**
   * Handle overlap resolution choice from modal
   */
  const handleOverlapResolve = useCallback(
    async (resolution: OverlapResolution) => {
      await performSubmission(resolution);
    },
    [performSubmission],
  );

  const handleSubmit = useCallback(async () => {
    if (!uploadedFile || !supplierId || !currency) {
      showToast({
        title: 'Missing Information',
        message: 'Please provide supplier, currency, and upload a CSV file.',
        color: 'error',
      });
      return;
    }

    try {
      // Convert edited data to CSV string (transformed)
      const transformedData = Papa.unparse(csvData);

      posthog.capture('manual_csv_submit_started', {
        context: 'manual_rebate_csv_upload',
        rowCount: csvData.length,
        supplier_id: supplierId,
        currency,
      });

      // Store pending submission data for potential retry after overlap resolution
      pendingSubmissionRef.current = { transformedData };

      // Check for overlapping data before submission
      const dateRange = extractDateRange(csvData);

      if (dateRange) {
        console.log(
          `Checking for overlaps: supplier=${supplierId}, range=${dateRange.startDate} to ${dateRange.endDate}`,
        );

        const overlapResult = await checkOverlap({
          supplierId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        });

        if (overlapResult.data.hasOverlap) {
          console.log(
            `Found ${overlapResult.data.overlappingInvoices.length} overlapping invoices`,
          );

          // Store overlap data and show modal
          setOverlappingInvoices(overlapResult.data.overlappingInvoices);
          setTotalAffectedLineItems(overlapResult.data.totalAffectedLineItems);
          setOverlapModalOpen(true);
          return; // Wait for user to resolve overlap
        }
      }

      // No overlap - proceed with submission
      await performSubmission();
    } catch (error) {
      console.error('Submit error:', error);

      const errorData = error as {
        response?: { data?: { error?: string } };
        message?: string;
      };

      const errorMessage =
        errorData?.response?.data?.error ||
        errorData?.message ||
        'Failed to submit CSV data';

      showToast({
        title: 'Submission Failed',
        message: errorMessage,
        color: 'error',
        persistent: true,
      });

      posthog.capture('manual_csv_submit_failed', {
        context: 'manual_rebate_csv_upload',
        error: errorMessage,
      });
    }
  }, [
    uploadedFile,
    supplierId,
    currency,
    csvData,
    checkOverlap,
    extractDateRange,
    performSubmission,
    showToast,
  ]);

  // UI state
  const canSubmit = useMemo(() => {
    return (
      supplierId &&
      currency &&
      csvData.length > 0 &&
      validationErrors.valid &&
      !isSubmitting &&
      !isCheckingOverlap &&
      !hasUnappliedConversionChanges
    );
  }, [
    supplierId,
    currency,
    csvData,
    validationErrors.valid,
    isSubmitting,
    isCheckingOverlap,
    hasUnappliedConversionChanges,
  ]);

  const uploadProgress = useMemo(() => {
    const total = csvData.length;
    const errors = validationErrors.rowErrors.length;
    const valid = total - errors;
    return { total, valid, errors };
  }, [csvData, validationErrors]);

  return (
    <DashboardLayout>
      <div className="py-6 pr-5 max-w-7xl">
        {/* Back Button */}
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Upload Options
        </Button>

        <h1 className="text-2xl font-bold mb-6">Manual CSV Upload</h1>

        {/* Step 1: Constants Form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                1
              </span>
              Supplier & Currency
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier" className="required">
                  Supplier
                </Label>
                <SupplierAutocomplete
                  supplierId={supplierId}
                  onSelect={setSupplierId}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency" className="required">
                  Currency
                </Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger id="currency">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_CODE_VALUES.map((curr) => (
                      <SelectItem key={curr} value={curr}>
                        {curr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conversion Rate (only if non-CAD currency selected) */}
            {currency && currency !== 'CAD' && (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                <Label
                  htmlFor="conversionRate"
                  className="flex items-center gap-2"
                >
                  💱 Conversion Rate to CAD (Optional)
                </Label>
                <Input
                  id="conversionRate"
                  type="number"
                  step="0.01"
                  placeholder="1.0"
                  value={conversionRate ?? ''}
                  onChange={(e) => {
                    const value = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setConversionRate(value);
                  }}
                  className="mt-2"
                />
                {conversionRate && (
                  <p className="text-sm text-gray-700 mt-2 font-medium">
                    1 {currency} = {conversionRate} CAD
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  If your CSV has a &quot;Value&quot; column, conversion will be
                  applied automatically when you upload.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Unapplied Conversion Changes Warning */}
        {hasUnappliedConversionChanges && hasValueColumn && (
          <Card className="mb-6 border-red-500 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <WarningCircleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-2">
                    Unapplied Currency Conversion Changes
                  </h3>
                  <p className="text-sm text-red-800 mb-2">
                    You&apos;ve changed the conversion rate to{' '}
                    <strong>{conversionRate}</strong>.
                    {appliedConversionRate !== undefined && (
                      <>
                        {' '}
                        The currently applied rate is{' '}
                        <strong>{appliedConversionRate}</strong>.
                      </>
                    )}
                    {appliedConversionRate === undefined && (
                      <> No conversion has been applied yet.</>
                    )}
                  </p>
                  <p className="text-sm text-red-800 mb-4">
                    You must apply the conversion before submitting.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleApplyConversion}
                      variant="default"
                      size="sm"
                    >
                      Apply Conversion
                    </Button>
                    <Button
                      onClick={handleResetConversion}
                      variant="outline"
                      size="sm"
                    >
                      Reset to{' '}
                      {appliedConversionRate !== undefined
                        ? appliedConversionRate
                        : 'No Conversion'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Conversion Applied Info */}
        {conversionApplied &&
          !hasUnappliedConversionChanges &&
          hasValueColumn && (
            <Alert className="mb-6 bg-blue-50 border-blue-200">
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>✓ Currency Conversion Applied</AlertTitle>
              <AlertDescription>
                <p className="mb-2">
                  Converted {currency} → CAD using rate{' '}
                  <strong>{appliedConversionRate}</strong>.
                </p>
                <p className="mt-1">Your table includes:</p>
                <ul className="list-disc ml-4 mt-1 space-y-0.5 text-sm">
                  <li>
                    <strong>Value {currency}</strong> - Original values
                  </li>
                  <li>
                    <strong>Conversion Rate to CAD</strong> - Rate used (
                    {appliedConversionRate})
                  </li>
                  <li>
                    <strong>Value</strong> - Converted values in CAD (rounded to
                    2 decimals)
                  </li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

        {/* Step 2: Template Download */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                2
              </span>
              Download Template
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <InfoIcon className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Download the CSV template with example data and proper column
                  formatting.
                </p>
                <div className="bg-muted p-3 rounded-md space-y-2">
                  <p className="text-sm font-medium">Required columns:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {templateInfo.requiredColumns.map((col) => (
                      <li key={col} className="flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                        <code className="bg-background px-1.5 py-0.5 rounded">
                          {col}
                        </code>
                        <span>- {templateInfo.examples[col]}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm font-medium mt-3">Optional columns:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {templateInfo.optionalColumns.map((col) => (
                      <li key={col} className="flex items-center gap-2">
                        <WarningCircleIcon className="h-4 w-4 text-blue-600" />
                        <code className="bg-background px-1.5 py-0.5 rounded">
                          {col}
                        </code>
                        <span>- {templateInfo.examples[col]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm font-semibold text-amber-900 mb-1">
                    📅 Important: Date Format
                  </p>
                  <p className="text-sm text-amber-800">
                    Dates must be in{' '}
                    <code className="bg-amber-100 px-1 py-0.5 rounded">
                      YYYY-MM-DD
                    </code>{' '}
                    format (e.g., 2025-01-31). If using Excel, format date cells
                    as Text before exporting to CSV, or use{' '}
                    <code className="bg-amber-100 px-1 py-0.5 rounded">
                      =TEXT(A1,&quot;YYYY-MM-DD&quot;)
                    </code>{' '}
                    to convert.
                  </p>
                </div>
                <Button onClick={handleDownloadTemplate} variant="outline">
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  Download Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: File Upload */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                3
              </span>
              Upload CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FileUploadDropzone
              onFilesSelected={handleFileUpload}
              uploadProgress={[]}
              isUploading={false}
              disabled={!supplierId || !currency}
              maxFiles={1}
              fillAvailableHeight={false}
              minHeight="200px"
            />
            {(!supplierId || !currency) && (
              <p className="text-sm text-muted-foreground mt-2">
                Please select supplier and currency first
              </p>
            )}
          </CardContent>
        </Card>

        {/* Step 4: Validation & Preview */}
        {csvData.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">
                  4
                </span>
                Review & Edit
                <div className="ml-auto flex items-center gap-2">
                  <Badge
                    variant={validationErrors.valid ? 'default' : 'destructive'}
                  >
                    {uploadProgress.valid} / {uploadProgress.total} valid
                  </Badge>
                  {uploadProgress.errors > 0 && (
                    <Badge variant="destructive">
                      {uploadProgress.errors} errors
                    </Badge>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Column-level errors */}
              {validationErrors.columnErrors.length > 0 && (
                <Alert variant="destructive">
                  <WarningCircleIcon className="h-4 w-4" />
                  <AlertTitle>Column Issues</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside space-y-1">
                      {validationErrors.columnErrors.map((error, idx) => (
                        <li key={idx} className="text-sm">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Row-level errors summary */}
              {validationErrors.rowErrors.length > 0 && (
                <Alert>
                  <WarningCircleIcon className="h-4 w-4" />
                  <AlertTitle>Data Validation</AlertTitle>
                  <AlertDescription>
                    <p className="text-sm mb-2">
                      Found {validationErrors.rowErrors.length} error(s) in your
                      data. Please review and fix the highlighted cells below.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {/* CSV Preview Table (editable - fix errors inline) */}
              <div className="border rounded-md overflow-auto max-h-[500px]">
                <SimpleDataTable
                  data={csvData}
                  columns={tableColumns}
                  getRowKey={(_, index) => `row-${index}`}
                  editable={true}
                  onCellChange={handleCellChange}
                />
              </div>

              {/* Progress bar */}
              {uploadProgress.total > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Validation Progress</span>
                    <span>
                      {uploadProgress.valid} / {uploadProgress.total} rows valid
                    </span>
                  </div>
                  <Progress
                    value={(uploadProgress.valid / uploadProgress.total) * 100}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit Button */}
        {csvData.length > 0 && (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setCsvData([]);
                setUploadedFile(null);
              }}
            >
              Clear
            </Button>
            <div className="relative group">
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                size="lg"
                className="min-w-[200px]"
              >
                {isSubmitting ? (
                  <>
                    <UploadIcon className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <UploadIcon className="mr-2 h-4 w-4" />
                    Submit {uploadProgress.valid} Line Items
                  </>
                )}
              </Button>
              {!canSubmit && csvData.length > 0 && (
                <div className="absolute bottom-full mb-2 right-0 bg-gray-900 text-white text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  {hasUnappliedConversionChanges
                    ? 'Apply conversion changes before submitting'
                    : !validationErrors.valid
                      ? 'Fix validation errors before submitting'
                      : 'Complete all required fields'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Overlap Warning Modal */}
      <OverlapWarningModal
        open={overlapModalOpen}
        onClose={() => {
          setOverlapModalOpen(false);
          pendingSubmissionRef.current = null;
        }}
        onResolve={handleOverlapResolve}
        overlappingInvoices={overlappingInvoices}
        totalAffectedLineItems={totalAffectedLineItems}
        isSubmitting={isSubmitting}
        supplierId={supplierId}
      />
    </DashboardLayout>
  );
};

export default ManualRebateCSVUpload;
